import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, RotateCcw, ArrowRight, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Reveal } from "./Reveal";
import { money } from "../lib/format";

const FAMILIES = ["floral", "dulce", "fresco", "amaderado"];

const QUESTIONS = {
  mujer: [
    {
      q: "¿Qué te describe mejor?",
      options: [
        { label: "Romántica y femenina", family: "floral" },
        { label: "Cálida y sensual", family: "dulce" },
        { label: "Fresca y natural", family: "fresco" },
        { label: "Elegante y misteriosa", family: "amaderado" },
      ],
    },
    {
      q: "¿Cuál es tu momento favorito?",
      options: [
        { label: "Una mañana entre flores", family: "floral" },
        { label: "Una noche de postres y velas", family: "dulce" },
        { label: "Un día de brisa y mar", family: "fresco" },
        { label: "Una cena de gala", family: "amaderado" },
      ],
    },
    {
      q: "¿Qué nota te enamora?",
      options: [
        { label: "Rosa y jazmín", family: "floral" },
        { label: "Vainilla y ámbar", family: "dulce" },
        { label: "Cítricos y aire limpio", family: "fresco" },
        { label: "Sándalo y maderas", family: "amaderado" },
      ],
    },
  ],
  hombre: [
    {
      q: "¿Qué te describe mejor?",
      options: [
        { label: "Clásico y sofisticado", family: "floral" },
        { label: "Cálido y magnético", family: "dulce" },
        { label: "Fresco y deportivo", family: "fresco" },
        { label: "Elegante y con carácter", family: "amaderado" },
      ],
    },
    {
      q: "¿Cuál es tu momento ideal?",
      options: [
        { label: "Un evento especial", family: "floral" },
        { label: "Una noche inolvidable", family: "dulce" },
        { label: "Una mañana al aire libre", family: "fresco" },
        { label: "Una reunión importante", family: "amaderado" },
      ],
    },
    {
      q: "¿Qué nota prefieres?",
      options: [
        { label: "Lavanda y toques florales", family: "floral" },
        { label: "Especias y vainilla", family: "dulce" },
        { label: "Cítricos y menta", family: "fresco" },
        { label: "Maderas y cuero", family: "amaderado" },
      ],
    },
  ],
};

const KEYWORDS = {
  floral: ["rosa", "flor", "floral", "jazm", "peon", "iris", "lavanda"],
  dulce: ["vainilla", "ámbar", "ambar", "dulce", "almizcle", "cál", "cal", "gourmand", "especia"],
  fresco: ["cítr", "citr", "fresco", "acuát", "acuat", "luna", "blanc", "polvo", "poudr", "menta"],
  amaderado: ["sánd", "sand", "madera", "amaderad", "bosque", "cuero"],
};

function recommend(products, family, gender) {
  const perfumes = products.filter(
    (p) => p.category === "mini-perfumes" && !p.is_upcoming && !p.is_giftset
  );
  if (!perfumes.length) return null;

  // Filter by selected gender (unisex products count for both)
  const byGender = perfumes.filter(
    (p) => !p.gender || p.gender === "unisex" || p.gender === gender
  );
  const pool = byGender.length ? byGender : perfumes;

  // Prefer products tagged with the winning scent family
  const tagged = pool.filter((p) => (p.scents || []).includes(family));
  if (tagged.length) return tagged.find((p) => p.featured) || tagged[0];

  // Fallback: keyword scoring
  const kws = KEYWORDS[family] || [];
  let best = null;
  let bestScore = -1;
  pool.forEach((p) => {
    const text = `${p.name} ${p.description}`.toLowerCase();
    const score = kws.reduce((s, k) => s + (text.includes(k) ? 1 : 0), 0);
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  });
  return best || pool.find((p) => p.featured) || pool[0];
}

export default function PerfumeQuiz({ products, currency, onOpen }) {
  const { addItem } = useCart();
  const [gender, setGender] = useState(null); // "mujer" | "hombre"
  const [step, setStep] = useState(-1); // -1 intro/gender, 0..2 questions, 3 result
  const [scores, setScores] = useState({ floral: 0, dulce: 0, fresco: 0, amaderado: 0 });
  const [result, setResult] = useState(null);

  const questions = gender ? QUESTIONS[gender] : [];

  const start = (g) => {
    setGender(g);
    setScores({ floral: 0, dulce: 0, fresco: 0, amaderado: 0 });
    setResult(null);
    setStep(0);
  };

  const answer = (family) => {
    const next = { ...scores, [family]: scores[family] + 1 };
    setScores(next);
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      const winner = FAMILIES.reduce((a, b) => (next[b] > next[a] ? b : a), FAMILIES[0]);
      setResult(recommend(products, winner, gender));
      setStep(3);
    }
  };

  const reset = () => {
    setGender(null);
    setResult(null);
    setScores({ floral: 0, dulce: 0, fresco: 0, amaderado: 0 });
    setStep(-1);
  };

  return (
    <section
      id="quiz"
      data-testid="quiz-section"
      className="bg-espresso text-cream py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-10 text-center min-h-[380px] flex flex-col items-center justify-center">
        <AnimatePresence mode="wait">
          {step === -1 && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
            >
              <Sparkles size={34} strokeWidth={1} className="mx-auto text-copper" />
              <span className="block mt-6 text-[11px] uppercase tracking-[0.3em] text-copper">
                Test olfativo
              </span>
              <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-none">
                Encuentra tu <span className="italic text-copper">aroma ideal</span>
              </h2>
              <p className="text-cream/60 mt-5 font-light max-w-md mx-auto">
                ¿Para quién es? Responde 3 preguntas rápidas y te recomendamos el mini perfume perfecto.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  data-testid="quiz-gender-mujer"
                  onClick={() => start("mujer")}
                  className="group inline-flex items-center gap-3 bg-cream text-espresso rounded-full pl-7 pr-5 py-4 text-xs uppercase tracking-[0.2em] hover:bg-copper transition-colors"
                >
                  Para ella
                  <span className="w-8 h-8 rounded-full bg-espresso/10 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                    <ArrowRight size={15} />
                  </span>
                </button>
                <button
                  data-testid="quiz-gender-hombre"
                  onClick={() => start("hombre")}
                  className="group inline-flex items-center gap-3 border border-cream/40 text-cream rounded-full pl-7 pr-5 py-4 text-xs uppercase tracking-[0.2em] hover:bg-cream hover:text-espresso transition-colors"
                >
                  Para él
                  <span className="w-8 h-8 rounded-full bg-cream/10 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                    <ArrowRight size={15} />
                  </span>
                </button>
              </div>
            </motion.div>
          )}

          {step >= 0 && step < 3 && (
            <motion.div
              key={`q-${step}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              className="w-full"
            >
              <span className="text-[11px] uppercase tracking-[0.3em] text-copper">
                {gender === "hombre" ? "Para él" : "Para ella"} · Pregunta {step + 1} / {questions.length}
              </span>
              <h3 className="mt-4 font-serif font-light text-3xl md:text-5xl leading-tight">
                {questions[step].q}
              </h3>
              <div className="mt-10 grid sm:grid-cols-2 gap-3">
                {questions[step].options.map((o, i) => (
                  <button
                    key={i}
                    data-testid={`quiz-option-${step}-${i}`}
                    onClick={() => answer(o.family)}
                    className="text-left px-6 py-5 border border-cream/20 rounded-xl hover:bg-cream hover:text-espresso transition-colors duration-300 font-light"
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              className="w-full"
              data-testid="quiz-result"
            >
              <span className="text-[11px] uppercase tracking-[0.3em] text-copper">
                Tu aroma ideal {gender === "hombre" ? "· para él" : "· para ella"}
              </span>
              {result ? (
                <div className="mt-6 bg-cream text-espresso rounded-2xl overflow-hidden flex flex-col sm:flex-row text-left max-w-xl mx-auto">
                  <div className="sm:w-1/2 aspect-square sm:aspect-auto overflow-hidden bg-surface">
                    <img src={result.image} alt={result.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="sm:w-1/2 p-6 flex flex-col">
                    <h3 className="font-serif text-3xl">{result.name}</h3>
                    <p className="text-taupe text-sm font-light mt-2 line-clamp-3">
                      {result.description}
                    </p>
                    <p className="mt-3 font-medium">
                      {money(
                        result.is_offer && result.offer_price ? result.offer_price : result.price,
                        currency
                      )}
                    </p>
                    <div className="mt-auto pt-5 flex flex-col gap-2">
                      <button
                        data-testid="quiz-add-cart"
                        onClick={() => addItem(result)}
                        className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors"
                      >
                        <Plus size={15} /> Agregar al carrito
                      </button>
                      <button
                        data-testid="quiz-view-detail"
                        onClick={() => onOpen && onOpen(result)}
                        className="w-full text-[11px] uppercase tracking-[0.2em] text-taupe hover:text-espresso transition-colors"
                      >
                        Ver detalle
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="mt-6 font-serif italic text-2xl">
                  Pronto tendremos una recomendación para ti.
                </p>
              )}
              <button
                data-testid="quiz-reset"
                onClick={reset}
                className="mt-8 inline-flex items-center gap-2 text-cream/70 text-[11px] uppercase tracking-[0.2em] hover:text-copper transition-colors"
              >
                <RotateCcw size={13} /> Repetir el test
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
